import { useMemo } from 'react';
import { getImageUrl } from '../../services/tmdb';
import { getChimeraScan } from '../../services/chimeraLens';
import type { Movie, TvShow, SearchResult, WatchProgress } from '../../types';
import WatchedButton from '../ui/WatchedButton';
import MatrixLink from '../ui/MatrixLink';

interface MediaCardProps {
  media: Movie | TvShow | SearchResult | WatchProgress;
  type?: 'movie' | 'tv';
  showProgress?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export default function MediaCard({ media, type, showProgress = false, size = 'md' }: MediaCardProps) {
  // Determine media type and ID
  const mediaType = type || ('media_type' in media ? media.media_type : ('title' in media ? 'movie' : 'tv'));
  const mediaId = 'mediaId' in media ? media.mediaId : media.id;
  const title = 'mediaId' in media ? media.title : ('title' in media ? media.title : (media as TvShow).name);
  const posterPath = 'poster_path' in media ? media.poster_path : ('posterPath' in media ? media.posterPath : null);

  // Handle vote_average which can be on different properties
  let voteAverage: number = 0;
  if ('vote_average' in media && typeof media.vote_average === 'number') {
    voteAverage = media.vote_average;
  }

  // Year for at-rest identity (gives the card a body before hover)
  let rawDate: string | undefined;
  if ('release_date' in media && media.release_date) {
    rawDate = media.release_date;
  } else if ('first_air_date' in media && (media as TvShow).first_air_date) {
    rawDate = (media as TvShow).first_air_date;
  }
  let year: number | null = null;
  if (rawDate) {
    const parsed = new Date(rawDate).getFullYear();
    if (!Number.isNaN(parsed)) year = parsed;
  }

  const progress = showProgress && 'progress' in media ? media.progress : 0;

  // Body-scan signal on hover — substrate-anchor visible where consciousness lingers.
  // Skip on continue-watching cards (already substrate-bearing via progress bar) and
  // skip when we lack the overview data needed for a substantive scan.
  const scanSignal = useMemo(() => {
    if (showProgress) return null;
    if (mediaType !== 'movie' && mediaType !== 'tv') return null;
    if (!('overview' in media) || !media.overview) return null;
    return getChimeraScan(media as Movie | TvShow, mediaType).signal;
  }, [showProgress, mediaType, mediaId, media]);

  const sizeClasses = {
    sm: 'w-32',
    md: 'w-40 sm:w-44 lg:w-48',
    lg: 'w-48 sm:w-56 lg:w-60',
  };

  if (mediaType === 'person') return null;

  return (
    <MatrixLink
      to={`/${mediaType}/${mediaId}`}
      titleForTransition={title || undefined}
      className={`media-card card-aura group relative flex-shrink-0 ${sizeClasses[size]} transition-all duration-500 ease-bounce hover:-translate-y-2.5 hover:scale-[1.03]`}
    >
      {/* Poster Container — soft ring at rest, deeper shadow lift on hover */}
      <div className="aspect-[2/3] relative overflow-hidden rounded-2xl bg-[#1c1814] ring-1 ring-white/[0.06] group-hover:ring-emerald-300/25 transition-shadow duration-500 shadow-[0_8px_28px_-12px_rgba(0,0,0,0.7)] group-hover:shadow-[0_2px_8px_rgba(0,0,0,.4),0_18px_40px_-12px_rgba(0,0,0,.85),0_0_24px_-6px_rgba(110,231,183,.25)]">
        {/* Image wrapper */}
        <div className="absolute inset-0 overflow-hidden rounded-2xl">
          <img
            src={getImageUrl(posterPath, 'w342')}
            alt={title || 'Media poster'}
            className="w-full h-full object-cover transition-all duration-500 group-hover:brightness-[0.55] group-hover:scale-[1.03]"
            loading="lazy"
            onError={(e) => {
              (e.target as HTMLImageElement).src = '/placeholder-poster.svg';
            }}
          />
        </div>

        {/* Body Scan radar sweep — emerald line sweeps down the poster on hover where a scan signal exists */}
        {scanSignal && (
          <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-2xl">
            <div className="scan-line" />
          </div>
        )}

        {/* Bottom atmospheric fade — bleeds the poster into the page so the card stops feeling like a hard rectangle on black */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/5 rounded-b-2xl bg-gradient-to-t from-[#0d0a08] via-[#0d0a08]/55 to-transparent" />

        {/* Hover overlay — adds substance to the bottom plate when active */}
        <div className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-t from-black/85 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400" />

        {/* Watched Button - Top right, visible on hover */}
        <div className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20">
          <WatchedButton
            mediaType={mediaType as 'movie' | 'tv'}
            id={mediaId}
            title={title || ''}
            posterPath={posterPath || undefined}
            variant="mini"
          />
        </div>

        {/* Rating Badge - Compact */}
        {voteAverage > 0 && (
          <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded bg-black/70 backdrop-blur-sm flex items-center gap-1 ring-1 ring-white/10">
            <svg className="w-2.5 h-2.5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            <span className="text-[10px] font-bold text-white">{voteAverage.toFixed(1)}</span>
          </div>
        )}

        {/* Play Icon - Centered, appears on hover */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-400">
          <div className="liquid-glass w-11 h-11 rounded-full flex items-center justify-center shadow-[0_8px_24px_rgba(0,0,0,0.5)] scale-75 group-hover:scale-100 transition-transform duration-400 ease-bounce">
            <svg className="w-4 h-4 text-white ml-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
            </svg>
          </div>
        </div>

        {/* Body-scan signal — surfaces on hover so substrate meets cursor where it lingers */}
        {scanSignal && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 px-3 pb-3 pt-6 opacity-0 group-hover:opacity-100 transition-opacity duration-400 bg-gradient-to-t from-black/95 via-black/70 to-transparent rounded-b-2xl">
            <div className="flex items-center gap-2">
              <span className="rounded-full border border-emerald-300/40 bg-emerald-300/10 px-1.5 py-[1px] text-[7px] font-semibold uppercase tracking-[0.18em] text-emerald-200 whitespace-nowrap">
                Scan
              </span>
              <p className="flex-1 text-[10.5px] leading-snug text-emerald-50/90 italic line-clamp-2">
                {scanSignal}
              </p>
            </div>
          </div>
        )}

        {/* Progress Bar */}
        {showProgress && progress > 0 && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/40 rounded-b-2xl overflow-hidden">
            <div
              className="h-full bg-[var(--color-primary)] transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
      </div>

      {/* Title plate — sits on the page-tone, so card + label reads as one body */}
      <div className="px-1 pt-2 pb-0.5">
        <h3 className="text-[13px] font-medium text-white/90 line-clamp-1 leading-snug group-hover:text-white transition-colors">{title}</h3>
        {year !== null && (
          <p className="text-[11px] text-white/40 mt-0.5 tabular-nums">{year}</p>
        )}
      </div>
    </MatrixLink>
  );
}
